import { ActionParamEncodingType } from './action-param-encoding-type'

/**
 * Data of the action that will be sent along with the event.
 *
 * @public
 */
export interface ActionEventData {

  /**
   * The HTTP method used for the action.
   */
  method?: string

  /**
   * The URL that is the target of the action
   */
  url?: string

  /**
   * The mimetype that will determine the format of the sent parameters
   */
  encoding?: ActionParamEncodingType

  /**
   * The set of parameters used for the action
   */
  params?: unknown
}

/**
 * Data containing the response value that was resolved from the execution of the action.
 *
 * @public
 */
export interface ActionCompletedEventData extends ActionEventData {
  value: unknown
}

/**
 * Data containing the error that was encountered while trying to execute the action.
 *
 * @public
 */
export interface ActionFailedEventData extends ActionEventData {
  error: Error
}

/**
 * The initial data required when creating a new instance of {@link ActionEvent}.
 *
 * @public
 */
export interface ActionEventInit extends EventInit, ActionEventData { }

/**
 * The initial data required when creating a new instance of {@link ActionCompletedEvent}.
 *
 * @public
 */
export interface ActionCompletedEventInit extends EventInit, ActionCompletedEventData { }

/**
 * The initial data required when creating a new instance of {@link ActionFailedEvent}.
 *
 * @public
 */
export interface ActionFailedEventInit extends EventInit, ActionFailedEventData { }

/**
 * The event that is fired when the execution of an action from the {@link ActionComponent} is started.
 *
 * @public
 */
export class ActionEvent extends Event {

  /**
   * The HTTP method used for the the action.
   */
  method?: string

  /**
   * The URL that will be sent an HTTP request when the action is executred
   */
  url?: string

  /**
   * The format in which the parameters are encoded
   */
  encoding?: ActionParamEncodingType

  /**
   * The set of parameters sent for the action
   */
  params?: unknown

  /**
   * The value of the response returned from the execution of the action.
   */
  value: unknown

  /**
   * The error that halted the execution of the action.
   */
  error: Error

  constructor(type: string, eventInitDict: ActionEventInit | ActionCompletedEventInit | ActionFailedEventInit) {
    super(type, eventInitDict)
    this.method = eventInitDict?.method
    this.url = eventInitDict?.url
    this.encoding = eventInitDict?.encoding
    this.params = eventInitDict?.params

    if ('value' in eventInitDict) {
      this.value = eventInitDict?.value
    }

    if ('error' in eventInitDict) {
      this.error = eventInitDict?.error
    }
  }

}
